import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import type { ChangeEvent } from 'react';
import Explanation from '@/components/exercise/explanation';
import RichContent from '@/components/shared/rich-content';
import { useQuestionMarking } from '@/hooks';
import { useCourses } from '@/providers';
import { useViewStore } from '@/stores';
import type { Question } from '@/types';
import { isExercise } from '@/types';
import { getCorrectLabels, isAnswerCorrect } from '@/utils/server/course';

export default function AnswersRadio({
  question,
  result,
  preview,
}: {
  question: Question;
  result?: boolean;
  preview?: boolean;
}) {
  const { selectedCourse, selectedChapter } = useCourses();
  const { showExplanations } = useViewStore();
  const { id, answers, marking, solution, type } = question;
  const [correctAnswerLabel, incorrectAnswerLabel] = getCorrectLabels();
  const isTrueFalse = type === 'tf';

  const { updateMarking } = useQuestionMarking(selectedCourse?.id || '');

  const answerClass = (answerIndex: number): string => {
    if (!solution) return '';
    const isCorrect = solution.indexOf(answerIndex.toString()) > -1;
    return `${result ? 'result' : ''} ${isCorrect ? 'correct' : 'incorrect'}`;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!selectedChapter || !isExercise(selectedChapter)) return;
    updateMarking({
      question,
      update: (event.target as HTMLInputElement).value,
      exercise: selectedChapter,
    });
  };

  const explanationIsActive = showExplanations.indexOf(id) > -1;

  return (
    <FormControl
      disabled={result || preview}
      sx={{
        '& .MuiFormControlLabel-root': {
          alignItems: 'flex-start',
          '& .MuiCheckbox-root': { paddingTop: 0 },
        },
        '&.result.correct .MuiFormControlLabel-label': { color: '#16C36C' },
        '&.result.incorrect .MuiFormControlLabel-label': { color: '#FC5055' },
      }}>
      <RadioGroup
        aria-label="question-radio"
        name={`question-${id}`}
        value={marking.length > 0 ? marking[0] : ''}
        onChange={handleChange}>
        {answers?.map(({ id, answerNode, explanationNode, index }) => (
          <Box
            key={id}
            className={answerClass(index)}
            sx={{
              '& .MuiFormControlLabel-root': {
                display: 'flex',
                alignItems: 'center',
              },
            }}>
            <FormControlLabel
              value={index.toString()}
              control={<Radio />}
              label={
                <Box
                  sx={{
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: result
                      ? (() => {
                          const isCorrectAnswer = isAnswerCorrect(type, index, solution || []);
                          return isCorrectAnswer ? '#16C36C' : '#FC5055';
                        })()
                      : undefined,
                  }}>
                  {isTrueFalse ? (
                    index === 0 ? (
                      incorrectAnswerLabel
                    ) : (
                      correctAnswerLabel
                    )
                  ) : (
                    <RichContent content={answerNode} noParagraphMargin />
                  )}
                  {result &&
                    (() => {
                      const isCorrectAnswer = isAnswerCorrect(type, index, solution || []);
                      return isCorrectAnswer ? (
                        <CheckCircle sx={{ color: '#16C36C', mt: 0.1 }} fontSize="small" />
                      ) : (
                        <Cancel sx={{ color: '#FC5055', mt: 0.1 }} fontSize="small" />
                      );
                    })()}
                </Box>
              }
            />
            {explanationIsActive && explanationNode && (
              <Box sx={{ mt: 1 }}>
                <Explanation
                  explanationNode={explanationNode}
                  isCorrect={isAnswerCorrect(type, index, solution || [])}
                />
              </Box>
            )}
          </Box>
        ))}
      </RadioGroup>
    </FormControl>
  );
}
